// Global state singleton for the SPA. Screens import this object and mutate
// it directly — keep it the only source of truth for cross-screen data.

/**
 * @typedef {object} User
 * @property {string|number} id
 * @property {string} email
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [name]
 * @property {string} [companyId]
 * @property {boolean} [emailVerified]
 * @property {string} [passwordUpdatedAt]
 */

/**
 * @typedef {object} OnboardingState
 * @property {string} companyName
 * @property {string} companyDisplayName
 * @property {string} billingAddress
 * @property {string} vatNumber
 * @property {string} companyRegistrationNumber
 * @property {string} accountHolderName
 * @property {string} accountNumber       8-digit UK account number (digits only)
 * @property {string} sortCode            6-digit UK sort code (digits only)
 * @property {string|null} logoBase64     data: URL or null
 * @property {string} invoiceNumberPrefix Default "INV"
 * @property {string} quoteNumberPrefix   Default "QU"
 * @property {number|null} invoiceNextNumber     the next-number setting; null follows on
 * @property {number|null} quoteNextNumber
 * @property {number|null} creditNoteNextNumber
 */

/**
 * @typedef {object} InvoiceItem
 * @property {string} description
 * @property {number} quantity
 * @property {number} unitPricePence  integer pence, as every amount on the wire
 */

/**
 * @typedef {object} Invoice
 * @property {string} id
 * @property {string|null} invoiceNumber  null on a draft: it takes one when it's sent (ADR-026)
 * @property {string} status            "draft" | "sent" | "partially_paid" | "paid" | "overdue" | "voided" | "written_off" | "superseded"
 * @property {string} issueDate         ISO date (YYYY-MM-DD)
 * @property {string} dueDate           ISO date (YYYY-MM-DD)
 * @property {string} [paidDate]
 * @property {number} subtotalPence   integer pence, as every amount on the wire
 * @property {number} taxPence
 * @property {number} totalPence
 * @property {number} [vatRate]       a rate, not money — worked back out of the totals
 * @property {InvoiceItem[]} [items]
 * @property {string} [contactId]
 * @property {string} [contactName]
 * @property {string} [paymentToken]
 * @property {string} [createdAt]
 */

/**
 * @typedef {object} Contact
 * @property {string} id
 * @property {string} name
 * @property {string} [email]
 * @property {string} [address]
 * @property {string} [createdAt]
 */

/**
 * @typedef {object} Quote
 * @property {string} id
 * @property {string|null} quoteNumber    null on a draft
 * @property {string} status            "draft" | "sent" | "accepted" | "declined" | "invoiced" ("expired" is derived from validUntil)
 * @property {string} issueDate         ISO date (YYYY-MM-DD)
 * @property {string} validUntil        ISO date (YYYY-MM-DD)
 * @property {number} totalPence      integer pence, as every amount on the wire
 * @property {string} [contactId]
 * @property {string} [contactName]
 * @property {string} [publicToken]
 * @property {string} [convertedInvoiceId]
 * @property {string} [createdAt]
 */

/**
 * @typedef {object} CreditNote
 * @property {string} id
 * @property {string|null} creditNoteNumber  null on a draft
 * @property {string} invoiceId
 * @property {string} status
 * @property {number} totalPence      integer pence, as every amount on the wire
 * @property {string} issueDate
 */

/**
 * @typedef {object} ReminderPolicy
 * @property {boolean} enabled
 * @property {number[]} pre_due_days
 * @property {boolean} send_on_due_date
 * @property {number[]} overdue_days
 * @property {string} send_time         "HH:MM" 24h
 * @property {string} timezone          IANA tz
 * @property {string} [subject_pre_due]
 * @property {string} [body_pre_due]
 * @property {string} [subject_on_due]
 * @property {string} [body_on_due]
 * @property {string} [subject_overdue]  legacy single overdue override (superseded by the per-tier fields below)
 * @property {string} [body_overdue]
 * @property {string} [subject_overdue_gentle]
 * @property {string} [body_overdue_gentle]
 * @property {string} [subject_overdue_firm]
 * @property {string} [body_overdue_firm]
 * @property {string} [subject_overdue_final]
 * @property {string} [body_overdue_final]
 */

/**
 * @typedef {object} EmailTemplate
 * @property {string} [subject]
 * @property {string} [body]
 */

/**
 * @typedef {object} Membership
 * @property {string} companyId
 * @property {"owner"|"admin"|"member"|"viewer"} role
 * @property {boolean} actorDisplayOn
 */

/**
 * @typedef {object} CompanyOption
 * @property {string} id
 * @property {string} name
 * @property {"owner"|"admin"|"member"|"viewer"} role
 */

/**
 * @typedef {object} AppState
 * @property {User|null} user
 * @property {Membership|null} membership  This user's role in the current company (multi-user & roles)
 * @property {CompanyOption[]} companies   Every active company this user belongs to (switcher, D16)
 * @property {OnboardingState} onboarding
 * @property {Contact[]} contacts
 * @property {Invoice[]} invoices
 * @property {Quote[]} quotes
 * @property {CreditNote[]} creditNotes
 * @property {{ drawerContactId: string|null }} contactsUi
 * @property {{ passwordResetEmail: string }} auth
 * @property {{ userId: string|null, email: string, purpose: string, inviteToken: string|null }} verification
 * @property {{ drawerType: string|null, activeTab: string }} profile
 * @property {{ drawerType: string|null, activeTab: string }} company
 * @property {ReminderPolicy|null} reminderPolicy
 * @property {EmailTemplate|null} emailTemplate
 * @property {EmailTemplate|null} quoteEmailTemplate
 * @property {object|null} numbering   what each kind is expected to be numbered next (lib/document-numbers.js)
 * @property {{ session: boolean, invoices: boolean, quotes: boolean, contacts: boolean, schedules: boolean }} _loaded
 * @property {Array<object>} [_schedulesCache]
 */

/** @type {AppState} */
export const state = {
  user: null,
  membership: null,
  companies: [],
  onboarding: {
    companyName: "",
    companyDisplayName: "",
    billingAddress: "",
    vatNumber: "",
    companyRegistrationNumber: "",
    accountHolderName: "",
    accountNumber: "",
    sortCode: "",
    logoBase64: null,
    invoiceNumberPrefix: "INV",
    quoteNumberPrefix: "QU",
    // The next-number setting: null follows on from the last number given out.
    invoiceNextNumber: null,
    quoteNextNumber: null,
    creditNoteNextNumber: null
  },
  contacts: [],
  invoices: [],
  quotes: [],
  creditNotes: [],
  // The number each kind of document is expected to take next, as the server
  // last said (`numbering` on the lists, the sends and GET /document-numbers):
  // { invoice, quote, creditNote }, each { expected, prefix, … }. What a draft
  // shows, since a draft holds no number (lib/document-numbers.js). null until
  // the server has said.
  numbering: null,
  contactsUi: {
    drawerContactId: null
  },
  auth: {
    passwordResetEmail: ""
  },
  verification: {
    userId: null,
    email: "",
    purpose: "",
    // Set only when the code screen was reached from an invite link. Logging
    // in ALWAYS detours through email verification, so without carrying the
    // token across that hop the invite is silently forgotten and the user
    // lands in whichever company they were already in.
    inviteToken: null,
    // The app route a sign-in was on the way to (/login?next=), carried
    // across the same hop for the same reason. Never set on signup: those
    // links go to accounts that already exist, and a new one starts on
    // Invoices.
    returnPath: null
  },
  profile: {
    drawerType: null,
    activeTab: "personal-info"
  },
  company: {
    drawerType: null,
    activeTab: "company-details"
  },
  // Company default email attachments (migration 063), metadata only, cached
  // per document kind. null = not fetched yet; the compose screens and the
  // Company drawer both read and refresh these.
  emailAttachmentDefaults: { invoice: null, quote: null },
  reminderPolicy: null,
  emailTemplate: null,
  quoteEmailTemplate: null,
  // Cached Sendinvo subscription state (from /auth/me at session load — see
  // applySubscription in lib/session.js). Used to decide whether the invoice
  // "Send" button should instead be a "Continue to subscribe" CTA: that's
  // `subscribed || inTrial`, not `subscribed` alone — a brand new signup is
  // `subscribed: false` but can send freely for the trial. All fields default
  // to their "not yet loaded" value until /auth/me responds.
  subscription: { subscribed: false, status: "none", inTrial: false, trialEndsAt: null, freeAccess: null },
  // Cached Stripe Connect state (from /stripe/connect/status). Populated
  // lazily by ensureStripeConnectStatus() — the compose preview needs it to
  // decide whether to show the "Pay online" CTA the client would really get.
  stripeConnect: null,
  // What GET /invoices last said about the books (#951): `{ provider,
  // providerName, heading }`, the heading the banner over the Invoices list
  // says while anything couldn't be added, or null with no books connected.
  // Read through widgets/accounting-attention.js.
  invoicesAccounting: null,
  // The Accounting section as GET /accounting last answered (#951):
  // `{ providers, connection }`. Read through screens/accounting.js.
  accounting: null,
  // What the server has switched on for this company (`features` on
  // /auth/me, ADR-039): `{ jobs, photos, jobCosts }`. Jobs reach the screens
  // only while `jobs` is true (lib/jobs-store.js's jobsOn), and costs on a
  // job only while `jobCosts` is too (widgets/job-costs.js's costsOn, ADR-054).
  features: { jobs: false, photos: false, jobCosts: false },
  // Every job in the company, archived ones too, each with what it is now
  // beside it (GET /jobs?archived=1), as lib/jobs-store.js last loaded them.
  jobs: [],
  _loaded: { session: false, invoices: false, quotes: false, contacts: false, schedules: false, stripeConnect: false, jobs: false }
};

const DUPLICATE_INVOICE_DRAFT_KEY = "sendinvo.duplicateInvoiceDraft";

/**
 * Persist a duplicate-invoice draft to sessionStorage so the create-invoice
 * route can pick it up after navigation.
 * @param {object|null} payload
 * @returns {void}
 */
// Apply what an invoice mutation answered with. The routes return the
// invoices they changed (`changedInvoices`, list shape) and any they deleted
// (`deletedInvoiceIds`); those rows are updated in place, and new ones go to
// the top, where the list's newest-first order puts them. A response carrying
// only the full `invoices` list (an older API during a deploy, or the demo)
// replaces the list as before. Returns state.invoices.
//
// Merging into a list that was never loaded leaves a partial list, which is
// safe only because every list screen refetches unless _loaded.invoices says
// the list is complete, and this never sets it.
export function applyInvoiceChanges(data) {
  if (Array.isArray(data?.changedInvoices)) {
    const changed = new Map(data.changedInvoices.map((inv) => [String(inv.id), inv]));
    const deleted = new Set((data.deletedInvoiceIds || []).map(String));
    const current = Array.isArray(state.invoices) ? state.invoices : [];
    const kept = current
      .filter((inv) => !deleted.has(String(inv.id)))
      .map((inv) => changed.get(String(inv.id)) || inv);
    const present = new Set(kept.map((inv) => String(inv.id)));
    const added = data.changedInvoices.filter((inv) => !present.has(String(inv.id)));
    state.invoices = [...added, ...kept];
  } else if (Array.isArray(data?.invoices)) {
    state.invoices = data.invoices;
  }
  return state.invoices;
}

export function setDuplicateInvoiceDraft(payload) {
  try {
    window.sessionStorage.setItem(DUPLICATE_INVOICE_DRAFT_KEY, JSON.stringify(payload || {}));
  } catch {
    // Ignore storage failures and fall back to opening an empty create form.
  }
}

/**
 * Read-and-clear the duplicate-invoice draft from sessionStorage.
 * @returns {object|null} the draft payload, or null if absent / unreadable
 */
export function popDuplicateInvoiceDraft() {
  try {
    const raw = window.sessionStorage.getItem(DUPLICATE_INVOICE_DRAFT_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(DUPLICATE_INVOICE_DRAFT_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

const DUPLICATE_QUOTE_DRAFT_KEY = "sendinvo.duplicateQuoteDraft";

/**
 * Persist a duplicate-quote draft to sessionStorage so the create-quote
 * route can pick it up after navigation.
 * @param {object|null} payload
 * @returns {void}
 */
export function setDuplicateQuoteDraft(payload) {
  try {
    window.sessionStorage.setItem(DUPLICATE_QUOTE_DRAFT_KEY, JSON.stringify(payload || {}));
  } catch {
    // Ignore storage failures and fall back to opening an empty create form.
  }
}

/**
 * Read-and-clear the duplicate-quote draft from sessionStorage.
 * @returns {object|null} the draft payload, or null if absent / unreadable
 */
export function popDuplicateQuoteDraft() {
  try {
    const raw = window.sessionStorage.getItem(DUPLICATE_QUOTE_DRAFT_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(DUPLICATE_QUOTE_DRAFT_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

const DUPLICATE_SCHEDULE_DRAFT_KEY = "sendinvo.duplicateScheduleDraft";

/**
 * Persist a duplicate-schedule draft to sessionStorage so the create-schedule
 * route can pick it up after navigation.
 * @param {object|null} payload
 * @returns {void}
 */
export function setDuplicateScheduleDraft(payload) {
  try {
    window.sessionStorage.setItem(DUPLICATE_SCHEDULE_DRAFT_KEY, JSON.stringify(payload || {}));
  } catch {
    // Ignore storage failures and fall back to opening an empty create form.
  }
}

/**
 * Read-and-clear the duplicate-schedule draft from sessionStorage.
 * @returns {object|null} the draft payload, or null if absent / unreadable
 */
export function popDuplicateScheduleDraft() {
  try {
    const raw = window.sessionStorage.getItem(DUPLICATE_SCHEDULE_DRAFT_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(DUPLICATE_SCHEDULE_DRAFT_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
